import { Trans } from '@lingui/macro'
import { Grid, Icon } from '@mui/material'
import { useSnackbar } from 'notistack'
import Dropzone, { DropzoneOptions } from 'react-dropzone'
import { dropboxStyles } from './UploadFilesField'

export interface FileWithData extends File {
  data: ArrayBuffer
  path?: string
}

interface FileDropzoneProps extends Omit<DropzoneOptions, 'onDrop' | 'maxFiles'> {
  /**
   * If dropzone should be disabled.
   */
  disabled?: boolean
  /**
   * Function that handles file upload.
   */
  handleUpload?: (file: FileWithData) => void
  /**
   * Maximum number of files that can be uploaded to the dropzone.
   * @default 1
   */
  maxFiles?: number
  /**
   * If dropzone should allow multiple files.
   * @default false
   */
  multiple?: boolean
}

/**
 * Styled file dropzone
 * @category Common components
 * @subcategory Inputs
 * @component
 * @returns {JSX.Element}
 */

/**
 * Styled file dropzone
 * @category Common components
 * @subcategory Inputs
 * @component
 * @returns {JSX.Element}
 */
export const FileDropzone: React.FC<FileDropzoneProps> = ({
  disabled = false,
  handleUpload,
  maxFiles = 1,
  multiple = false,
  ...props
}) => {
  const classes = dropboxStyles()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const showError = (e): void => {
    console.log('file reading has failed', e)
    enqueueSnackbar(<Trans>Error Uploaded File</Trans>, {
      variant: 'error'
    })
  }

  const handleOnDrop = (files, e): void => {
    const file = files[0]
    if (file == null) {
      showError(e)
      return
    }
    const reader = new FileReader()
    reader.onabort = () => console.log('file reading was aborted')
    reader.onerror = e => {
      showError(e)
    }
    reader.onload = () => {
      const binaryStr = reader.result as ArrayBuffer
      const data = reader.result
      if (handleUpload != null) {
        handleUpload({ ...file, data })
      }
      const uploadingKey = enqueueSnackbar(<Trans>FILE_UPLOAD_SUCCESS</Trans>, {
        variant: 'info'
      })
      const handleError = (): void => {
        closeSnackbar(uploadingKey)
      }
      if (binaryStr != null && binaryStr.byteLength > 52428800) {
        enqueueSnackbar(<Trans>Maximum file size is 52 MB</Trans>, {
          variant: 'error'
        })
        handleError()
      }
    }
    reader.readAsArrayBuffer(file)
  }

  return (
    <Dropzone
      disabled={disabled}
      maxFiles={maxFiles}
      multiple={multiple}
      onDrop={handleOnDrop}
      {...props}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          className={disabled ? classes.disabled : classes.dropbox}
        >
          <section>
            <input {...getInputProps()} />
            <div
              style={{
                textAlign: 'center'
              }}
            >
              <Grid
                container
                direction='row'
                alignItems='center'
                justifyContent='center'
              >
                <Icon style={{ marginRight: 10 }}>upload</Icon>
                <Trans>Drag 'n' drop file here, or click to select file</Trans>
              </Grid>
            </div>

            <div />
          </section>
        </div>
      )}
    </Dropzone>
  )
}
